import type { DossierDe, JourDuCalendrier } from '../partage/api/types';

/**
 * La semaine de l'Agenda au téléphone en cartes (chantier `telephone-journee-agenda`, ticket 05,
 * décision 17 de la revue, maquette `design/telephone.html`, piste « Agenda », « La semaine ») :
 * chaque chose dans une carte au ton clair de la couleur de son projet, son signe en tête, sans
 * légende. Rien n'est calculé ici que l'ordre d'affichage et le filtre par projet : les jours,
 * les heures, les chevauchements et les réponses pas venues viennent du serveur
 * (`GET /api/calendrier`).
 */

export type SorteDeCarte = 'rdv' | 'echeance' | 'attente' | 'journee';

/** Le signe en tête de chaque carte : il remplace la légende. */
export const SIGNES: Record<SorteDeCarte, string> = { rdv: '●', echeance: '⚑', attente: '⏸', journee: '◷' };

export interface CarteDuJour {
  cle: string;
  sorte: SorteDeCarte;
  /** La bulle qu'elle ouvre ; `null` pour ta journée, qui n'est pas une bulle. */
  bulleId: string | null;
  titre: string;
  /** L'heure d'un rendez-vous, ou celle où ta journée commence. */
  heure: string | null;
  /** Son projet, qui donne sa couleur ; `null` pour ta journée (à toi, en vert). */
  projet: DossierDe;
  chevauche: boolean;
  pasVenue: boolean;
  /** Combien de tâches dans ta journée. */
  combien: number;
}

/**
 * Le jour vu d'un seul projet (« Tous les projets ▾ ») : ses rendez-vous, ses échéances, ses
 * réponses attendues, et ta journée réduite à ses tâches (rien s'il n'y en a pas). Sans projet
 * choisi, le jour tel quel. Une chose sans projet ne passe aucun filtre.
 */
export function jourDuProjet(j: JourDuCalendrier, projet: string | null): JourDuCalendrier {
  if (projet === null) return j;
  const duProjet = (d: DossierDe | undefined) => d?.id === projet;
  const taches = (j.journee?.taches ?? []).filter((t) => duProjet(t.dossier));
  return {
    ...j,
    echeances: j.echeances.filter((e) => duProjet(e.dossier)),
    rendezVous: j.rendezVous.filter((r) => duProjet(r.dossier)),
    attentes: j.attentes.filter((a) => duProjet(a.dossier)),
    journee: j.journee && taches.length > 0 ? { ...j.journee, taches } : null,
  };
}

const carte = (c: Partial<CarteDuJour> & Pick<CarteDuJour, 'cle' | 'sorte'>): CarteDuJour => ({
  bulleId: null,
  titre: '',
  heure: null,
  projet: null,
  chevauche: false,
  pasVenue: false,
  combien: 0,
  ...c,
});

/**
 * Les cartes d'un jour, dans l'ordre : les rendez-vous à leur heure (le serveur les range), les
 * échéances, les réponses attendues (celles qui ne sont pas venues d'abord : elles appellent un
 * geste), puis ta journée s'il y a des tâches dedans.
 */
export function cartesDuJour(j: JourDuCalendrier): CarteDuJour[] {
  const taches = j.journee?.taches ?? [];
  const attentes = [...j.attentes.filter((a) => a.pasVenue), ...j.attentes.filter((a) => !a.pasVenue)];
  return [
    ...j.rendezVous.map((r) =>
      carte({ cle: `r-${r.bulleId}`, sorte: 'rdv', bulleId: r.bulleId, titre: r.titre, heure: r.heure, projet: r.dossier, chevauche: r.chevauche })
    ),
    ...j.echeances.map((e) => carte({ cle: `e-${e.bulleId}`, sorte: 'echeance', bulleId: e.bulleId, titre: e.titre, projet: e.dossier })),
    ...attentes.map((a) =>
      carte({ cle: `a-${a.bulleId}`, sorte: 'attente', bulleId: a.bulleId, titre: a.titre, projet: a.dossier, pasVenue: a.pasVenue })
    ),
    ...(taches.length > 0
      ? [carte({ cle: 'journee', sorte: 'journee', heure: taches[0]?.heure ?? j.journee?.debut ?? null, combien: taches.length })]
      : []),
  ];
}

/**
 * Ce que fait le toucher d'une carte : sa fiche ; une réponse pas venue montre d'abord
 * « Relancer » et « C'est arrivé » ; ta journée ouvre l'onglet Journée aujourd'hui, le jour ouvert
 * un autre jour.
 */
export type ToucherDUneCarte = 'fiche' | 'gestes' | 'onglet-journee' | 'jour';

export function toucherDUneCarte(c: CarteDuJour, j: Pick<JourDuCalendrier, 'estAujourdhui'>): ToucherDUneCarte {
  if (c.sorte === 'journee') return j.estAujourdhui ? 'onglet-journee' : 'jour';
  if (c.sorte === 'attente' && c.pasVenue) return 'gestes';
  return 'fiche';
}
